import type { MouseEvent, TouchEvent } from "react";
import type { Node, Range } from "slate";
import { Editor, Element, Path, Transforms } from "slate";

import type {
  EditorElement,
  EditorElementType,
  ParagraphTag,
} from "@smart/itops-serialisation-basic";

import { isBlockActive } from "./actions";
import type { FormatFeature } from "./types";
import { IconName } from "../../icon";

export type TextStyleTag = "p" | "h1" | "h2" | "h3";

export const textStyleOptions: { label: string; value: TextStyleTag }[] = [
  { label: "Normal", value: "p" },
  { label: "Heading 3", value: "h3" },
  { label: "Heading 2", value: "h2" },
  { label: "Heading 1", value: "h1" },
];

export const getLinkAtSelection = (
  editor: Editor,
  selectionOverride?: Range | null,
): [EditorElement<"link">, Path] | undefined => {
  const selection = selectionOverride ?? editor.selection;
  if (!selection) return undefined;
  const [entry] = Editor.nodes(editor, {
    at: Editor.unhangRange(editor, selection),
    match: (n) =>
      !Editor.isEditor(n) && Element.isElement(n) && n.type === "link",
  });
  if (!entry) return undefined;
  const [node, path] = entry;
  return [node as EditorElement<"link">, path];
};

export const getLinkDisplayTextAtSelection = (
  editor: Editor,
  selectionOverride?: Range | null,
): string | undefined => {
  const entry = getLinkAtSelection(editor, selectionOverride);
  if (!entry) return undefined;
  const [, path] = entry;
  return Editor.string(editor, path);
};

export type ListType = "bulleted-list" | "numbered-list";

const isListElement = (n: Node): n is Element & { type: ListType } =>
  Element.isElement(n) &&
  (n.type === "bulleted-list" || n.type === "numbered-list");

export const toggleList =
  (
    editor: Editor,
    listType: ListType,
  ): ((e: MouseEvent | TouchEvent) => void) =>
  (e) => {
    e.preventDefault();
    const { selection } = editor;
    if (!selection) return;
    const at = Editor.unhangRange(editor, selection);

    const blocks = Array.from(
      Editor.nodes(editor, {
        at,
        match: (n) =>
          Element.isElement(n) &&
          (n.type === "paragraph" || n.type === "list-item"),
      }),
    );
    if (blocks.length === 0) return;

    const parentLists = new Map<string, Path>();
    let allListItems = true;
    let allInTargetList = true;
    for (const [node, path] of blocks) {
      if (Element.isElement(node) && node.type === "list-item") {
        const [parent, parentPath] = Editor.parent(editor, path);
        if (isListElement(parent)) {
          parentLists.set(parentPath.join(","), parentPath);
          if (parent.type !== listType) allInTargetList = false;
        }
      } else {
        allListItems = false;
        allInTargetList = false;
      }
    }

    // untoggle, every touched block already this list type
    if (allInTargetList) {
      Editor.withoutNormalizing(editor, () => {
        // set_node keeps paths so at stays valid for unwrap
        Transforms.setNodes(
          editor,
          { type: "paragraph" },
          { at, match: (n) => Element.isElement(n) && n.type === "list-item" },
        );
        Transforms.unwrapNodes(editor, {
          at,
          match: (n) => Element.isElement(n) && n.type === listType,
          split: true,
        });
      });
      return;
    }

    // only list items with an opposite type, convert wrappers in place
    if (allListItems) {
      for (const listPath of parentLists.values()) {
        const [list] = Editor.node(editor, listPath);
        if (isListElement(list) && list.type !== listType) {
          Transforms.setNodes(editor, { type: listType }, { at: listPath });
        }
      }
      return;
    }

    // mixed paragraphs and list items, merge into one list
    Editor.withoutNormalizing(editor, () => {
      const rangeRef = Editor.rangeRef(editor, at, { affinity: "inward" });
      Transforms.unwrapNodes(editor, {
        at: rangeRef.current ?? at,
        match: isListElement,
        split: true,
      });
      Transforms.setNodes(
        editor,
        { type: "list-item", paragraphTag: undefined },
        {
          at: rangeRef.current ?? at,
          match: (n) =>
            Element.isElement(n) &&
            (n.type === "paragraph" || n.type === "list-item"),
        },
      );
      Transforms.wrapNodes(
        editor,
        { type: listType, children: [] },
        {
          at: rangeRef.current ?? at,
          match: (n) => Element.isElement(n) && n.type === "list-item",
        },
      );
      rangeRef.unref();
    });
  };

export const toggleBulletedList = (editor: Editor) =>
  toggleList(editor, "bulleted-list");

export const toggleNumberedList = (editor: Editor) =>
  toggleList(editor, "numbered-list");

export const toggleBlockquote =
  (editor: Editor): ((e: MouseEvent | TouchEvent) => void) =>
  (e) => {
    e.preventDefault();
    const { selection } = editor;
    if (!selection) return;

    const isActive = isBlockActive(editor, ["blockquote"]);
    if (isActive) {
      Transforms.unwrapNodes(editor, {
        match: (n) => Element.isElement(n) && n.type === "blockquote",
        split: true,
      });
    } else {
      Transforms.wrapNodes(editor, {
        type: "blockquote",
        children: [],
      });
    }
  };

export const toTextStyleTag = (tag: ParagraphTag | undefined): TextStyleTag =>
  tag === "h1" || tag === "h2" || tag === "h3" ? tag : "p";

// selection overrides can be cached ranges that outlive a document change
const isRangeInDocument = (editor: Editor, range: Range): boolean =>
  Editor.hasPath(editor, range.anchor.path) &&
  Editor.hasPath(editor, range.focus.path);

export const hasMixedParagraphStyles = (
  editor: Editor,
  selectionOverride?: Range | null,
): boolean => {
  const selection = selectionOverride ?? editor.selection;
  if (!selection || !isRangeInDocument(editor, selection)) return false;

  const tags = new Set<TextStyleTag>();
  for (const [node] of Editor.nodes(editor, {
    at: Editor.unhangRange(editor, selection),
    match: (n) => Element.isElement(n) && n.type === "paragraph",
  })) {
    const tag = toTextStyleTag(
      (node as EditorElement<"paragraph">).paragraphTag,
    );
    tags.add(tag);
    if (tags.size > 1) return true;
  }
  return false;
};

export const getParagraphTag = (
  editor: Editor,
  selectionOverride?: Range | null,
): ParagraphTag | undefined => {
  const selection = selectionOverride ?? editor.selection;
  if (!selection || !isRangeInDocument(editor, selection)) return undefined;

  const [entry] = Editor.nodes(editor, {
    at: Editor.unhangRange(editor, selection),
    match: (n) => Element.isElement(n) && n.type === "paragraph",
  });
  if (!entry) return undefined;

  const [node] = entry;
  return (node as EditorElement<"paragraph">).paragraphTag;
};

export const setParagraphTag =
  (
    editor: Editor,
    paragraphTag: ParagraphTag,
    selectionOverride?: Range | null,
  ): (() => void) =>
  () => {
    const selection = selectionOverride ?? editor.selection;
    if (!selection || !isRangeInDocument(editor, selection)) return;
    Transforms.setNodes(
      editor,
      { paragraphTag },
      {
        at: Editor.unhangRange(editor, selection),
        match: (n) => Element.isElement(n) && n.type === "paragraph",
      },
    );
  };

const positionCursor = (editor: Editor, path: Path): void => {
  const start = Editor.start(editor, path);
  const blockText = Editor.string(editor, path);
  let cursorOffset = 0;
  while (cursorOffset < blockText.length && blockText[cursorOffset] === "\t") {
    cursorOffset += 1;
  }
  const cursorPoint = { ...start, offset: cursorOffset };
  Transforms.select(editor, { anchor: cursorPoint, focus: cursorPoint });
};

export const applyIndent = (
  editor: Editor,
  selectionOverride?: Range | null,
): void => {
  const selection = selectionOverride ?? editor.selection;
  if (!selection) return;

  const [entry] = Editor.nodes(editor, {
    at: Editor.unhangRange(editor, selection),
    match: (n) =>
      Element.isElement(n) &&
      (n.type === "paragraph" || n.type === "list-item"),
  });
  if (!entry) return;

  const [, path] = entry;
  const start = Editor.start(editor, path);
  Transforms.insertText(editor, "\t", { at: start });
  positionCursor(editor, path);
};

export const applyOutdent = (
  editor: Editor,
  selectionOverride?: Range | null,
): void => {
  const selection = selectionOverride ?? editor.selection;
  if (!selection) return;

  const [entry] = Editor.nodes(editor, {
    at: Editor.unhangRange(editor, selection),
    match: (n) =>
      Element.isElement(n) &&
      (n.type === "paragraph" || n.type === "list-item"),
  });
  if (!entry) return;

  const [, path] = entry;
  const start = Editor.start(editor, path);
  const blockText = Editor.string(editor, path);
  if (!blockText.startsWith("\t")) return;

  const end = { ...start, offset: start.offset + 1 };
  Transforms.delete(editor, { at: { anchor: start, focus: end } });
  positionCursor(editor, path);
};

export type BlockFormatOption = "blockquote" | "numberedList" | "bulletedList";

export const blockFormats: Record<
  BlockFormatOption,
  {
    icon: IconName;
    text: string;
    feature: FormatFeature;
    activeTypes: EditorElementType[];
    toggle: (editor: Editor) => (e: MouseEvent | TouchEvent) => void;
  }
> = {
  blockquote: {
    icon: "regularBlockQuote",
    text: "Accent Line",
    feature: "blockquote",
    activeTypes: ["blockquote"],
    toggle: toggleBlockquote,
  },
  numberedList: {
    icon: "regularList12",
    text: "Numbered List",
    feature: "lists",
    activeTypes: ["numbered-list"],
    toggle: toggleNumberedList,
  },
  bulletedList: {
    icon: "regularListUl",
    text: "Bulleted List",
    feature: "lists",
    activeTypes: ["bulleted-list"],
    toggle: toggleBulletedList,
  },
};

export const indentFormats: Record<
  "outdent" | "indent",
  {
    icon: IconName;
    text: string;
    apply: (editor: Editor, selectionOverride?: Range | null) => void;
  }
> = {
  outdent: {
    icon: "regularOutdent",
    text: "Decrease Indent",
    apply: applyOutdent,
  },
  indent: {
    icon: "regularIndent",
    text: "Increase Indent",
    apply: applyIndent,
  },
};
